import { supabase } from '@/lib/supabase'
import type {
  AircraftTypeRow,
  AircraftTypeInsert,
  AircraftTypeUpdate,
  AircraftTypeFilters,
} from '@/types/aircraft-types'

/**
 * Fetch all aircraft types with optional filtering
 * Aircraft types are global (no org_id) - all authenticated users can read
 */
export async function fetchAircraftTypes(
  filters?: AircraftTypeFilters
): Promise<AircraftTypeRow[]> {
  let query = supabase
    .from('aircraft_types')
    .select('*')
    .order('manufacturer')
    .order('model')

  // Apply filters
  if (filters?.search) {
    query = query.or(
      `manufacturer.ilike.%${filters.search}%,model.ilike.%${filters.search}%,type_code.ilike.%${filters.search}%,icao_designator.ilike.%${filters.search}%`
    )
  }

  if (filters?.category) {
    query = query.eq('category', filters.category)
  }

  if (filters?.isActive !== undefined) {
    query = query.eq('is_active', filters.isActive)
  }

  const { data, error } = await query

  if (error) throw error
  return (data as unknown as AircraftTypeRow[]) || []
}

/**
 * Fetch a single aircraft type by ID
 */
export async function fetchAircraftType(id: string): Promise<AircraftTypeRow> {
  const { data, error } = await supabase
    .from('aircraft_types')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as unknown as AircraftTypeRow
}

/**
 * Fetch aircraft type by type_code
 */
export async function fetchAircraftTypeByCode(
  typeCode: string
): Promise<AircraftTypeRow | null> {
  const { data, error } = await supabase
    .from('aircraft_types')
    .select('*')
    .eq('type_code', typeCode)
    .maybeSingle()

  if (error) throw error
  return data as unknown as AircraftTypeRow
}

/**
 * Fetch aircraft type by ICAO designator
 */
export async function fetchAircraftTypeByIcao(
  icaoDesignator: string
): Promise<AircraftTypeRow | null> {
  const { data, error } = await supabase
    .from('aircraft_types')
    .select('*')
    .eq('icao_designator', icaoDesignator.toUpperCase())
    .maybeSingle()

  if (error) throw error
  return data as unknown as AircraftTypeRow
}

/**
 * Check if an aircraft type with the same manufacturer/model/variant exists
 * Used for duplicate detection before creating a new aircraft type
 * Case-insensitive comparison to match DB constraint
 */
export async function checkAircraftTypeExists(
  manufacturer: string,
  model: string,
  variant?: string
): Promise<AircraftTypeRow | null> {
  // Build query with case-insensitive matching
  let query = supabase
    .from('aircraft_types')
    .select('*')
    .ilike('manufacturer', manufacturer)
    .ilike('model', model)

  // Handle variant matching (NULL vs value)
  if (variant) {
    query = query.ilike('variant', variant)
  } else {
    query = query.is('variant', null)
  }

  const { data, error } = await query.maybeSingle()

  if (error) throw error
  return data as unknown as AircraftTypeRow
}

/**
 * Create a new aircraft type (admin only)
 */
export async function createAircraftType(
  data: AircraftTypeInsert
): Promise<AircraftTypeRow> {
  const { data: created, error } = await supabase
    .from('aircraft_types')
    .insert({
      type_code: data.type_code,
      manufacturer: data.manufacturer,
      model: data.model,
      variant: data.variant || null,
      category: data.category || null,
      length: data.length,
      wingspan: data.wingspan,
      height: data.height,
      tail_height: data.tail_height ?? null,
      empty_weight: data.empty_weight ?? null,
      max_takeoff_weight: data.max_takeoff_weight ?? null,
      max_ramp_weight: data.max_ramp_weight ?? null,
      model_file_path: data.model_file_path ?? null,
      model_scale: data.model_scale ?? 1.0,
      heading_axis: data.heading_axis ?? 'y',
      vertical_axis: data.vertical_axis ?? 'z',
      typical_value_new: data.typical_value_new ?? null,
      typical_value_used: data.typical_value_used ?? null,
      hangar_rate_typical: data.hangar_rate_typical ?? null,
      notes: data.notes ?? null,
      is_active: data.is_active ?? true,
      // Landing gear / towing fields
      gear_type: data.gear_type ?? null,
      main_gear_wheels: data.main_gear_wheels ?? null,
      nose_gear_steering_angle: data.nose_gear_steering_angle ?? null,
      nose_gear_from_nose: data.nose_gear_from_nose ?? null,
      main_gear_from_nose: data.main_gear_from_nose ?? null,
      wheel_track: data.wheel_track ?? null,
      wheelbase: data.wheelbase ?? null,
      turning_radius: data.turning_radius ?? null,
      // External API reference
      icao_designator: data.icao_designator?.toUpperCase() ?? null,
      external_api_id: data.external_api_id ?? null,
      last_api_sync: data.last_api_sync ?? null,
    })
    .select()
    .single()

  if (error) throw error
  return created as unknown as AircraftTypeRow
}

/**
 * Update an existing aircraft type (admin only)
 */
export async function updateAircraftType(
  id: string,
  data: AircraftTypeUpdate
): Promise<AircraftTypeRow> {
  const updateData: Record<string, unknown> = {}

  // Basic fields
  if (data.type_code !== undefined) updateData.type_code = data.type_code
  if (data.manufacturer !== undefined) updateData.manufacturer = data.manufacturer
  if (data.model !== undefined) updateData.model = data.model
  if (data.variant !== undefined) updateData.variant = data.variant
  if (data.category !== undefined) updateData.category = data.category

  // Dimensions
  if (data.length !== undefined) updateData.length = data.length
  if (data.wingspan !== undefined) updateData.wingspan = data.wingspan
  if (data.height !== undefined) updateData.height = data.height
  if (data.tail_height !== undefined) updateData.tail_height = data.tail_height

  // Weight
  if (data.empty_weight !== undefined) updateData.empty_weight = data.empty_weight
  if (data.max_takeoff_weight !== undefined)
    updateData.max_takeoff_weight = data.max_takeoff_weight
  if (data.max_ramp_weight !== undefined)
    updateData.max_ramp_weight = data.max_ramp_weight

  // 3D Model
  if (data.model_file_path !== undefined)
    updateData.model_file_path = data.model_file_path
  if (data.model_scale !== undefined) updateData.model_scale = data.model_scale
  if (data.heading_axis !== undefined) updateData.heading_axis = data.heading_axis
  if (data.vertical_axis !== undefined) updateData.vertical_axis = data.vertical_axis

  // Pricing
  if (data.typical_value_new !== undefined)
    updateData.typical_value_new = data.typical_value_new
  if (data.typical_value_used !== undefined)
    updateData.typical_value_used = data.typical_value_used
  if (data.hangar_rate_typical !== undefined)
    updateData.hangar_rate_typical = data.hangar_rate_typical

  // Metadata
  if (data.notes !== undefined) updateData.notes = data.notes
  if (data.is_active !== undefined) updateData.is_active = data.is_active

  // Landing gear / towing fields
  if (data.gear_type !== undefined) updateData.gear_type = data.gear_type
  if (data.main_gear_wheels !== undefined)
    updateData.main_gear_wheels = data.main_gear_wheels
  if (data.nose_gear_steering_angle !== undefined)
    updateData.nose_gear_steering_angle = data.nose_gear_steering_angle
  if (data.nose_gear_from_nose !== undefined)
    updateData.nose_gear_from_nose = data.nose_gear_from_nose
  if (data.main_gear_from_nose !== undefined)
    updateData.main_gear_from_nose = data.main_gear_from_nose
  if (data.wheel_track !== undefined) updateData.wheel_track = data.wheel_track
  if (data.wheelbase !== undefined) updateData.wheelbase = data.wheelbase
  if (data.turning_radius !== undefined)
    updateData.turning_radius = data.turning_radius

  // External API reference
  if (data.icao_designator !== undefined)
    updateData.icao_designator = data.icao_designator?.toUpperCase() ?? null
  if (data.external_api_id !== undefined)
    updateData.external_api_id = data.external_api_id
  if (data.last_api_sync !== undefined) updateData.last_api_sync = data.last_api_sync

  const { data: updated, error } = await supabase
    .from('aircraft_types')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return updated as unknown as AircraftTypeRow
}

/**
 * Delete an aircraft type (admin only)
 * Note: This is a hard delete since aircraft_types is reference data
 */
export async function deleteAircraftType(id: string): Promise<void> {
  const { error } = await supabase.from('aircraft_types').delete().eq('id', id)

  if (error) throw error
}
